import { Settings } from '@mui/icons-material';
import { Box, Button, IconButton, Slider, Switch } from '@mui/material';
import React, { useState } from 'react';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import { DEFAULT_CANVAS_PREFERENCES } from '../../pedigree-utils';
import { computeAutoSpacing } from '../pedigree/autoSpacing';

const CARD_SX = {
  bgcolor: '#fff',
  border: '0.5px solid',
  borderColor: 'divider',
  borderRadius: '12px',
  overflow: 'hidden'
};

const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.disabled',
  mb: 1
};

const ROW_LABEL_W = 160;

const ConfigRow = ({ label, isLast, children }) => (
  <Box
    sx={{
      px: 2.5,
      py: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderBottom: isLast ? 'none' : '0.5px solid',
      borderColor: 'divider',
      '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' }
    }}
  >
    <Box
      sx={{
        width: ROW_LABEL_W,
        flexShrink: 0,
        fontSize: 14,
        fontWeight: 500,
        color: 'text.primary'
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      {children}
    </Box>
  </Box>
);

function CanvasPreferences({
  options,
  setOptions,
  members = [],
  buttonRight,
  buttonLeft,
  inline = false
}) {
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    DEFAULT_CANVAS_PREFERENCES
  });

  React.useMemo(() => {
    setPreferences(options);
  }, [options]);

  const { setSnack } = React.useContext(SnackContext);

  const onSave = async () => {
    return axios
      .put(APICONSTANTS.updatePreferences, {
        pedigreeSettings: preferences
      })
      .then((res) => {
        if (res.status === 200) {
          setOptions((prev) => ({ ...prev, ...preferences }));
          setSnack({
            status: true,
            msg: 'Pedigree preferences saved!',
            severity: 'success'
          });
        }
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'Failed to save pedigree preferences.',
          severity: 'error'
        });
      })
      .finally(() => setOpen(false));
  };

  return (
    <>
      {inline ? (
        <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
          <Settings sx={{ fontSize: 22, color: '#3d4551' }} />
        </IconButton>
      ) : (
        <Box
          {...(buttonLeft !== undefined
            ? { left: buttonLeft }
            : { right: buttonRight })}
          sx={{
            position: 'absolute',
            top: 12,
            pointerEvents: 'auto',
            zIndex: 1000
          }}
        >
          <IconButton onClick={() => setOpen((prev) => !prev)}>
            <Settings color="secondary" />
          </IconButton>
        </Box>
      )}
      <CustomDialog
        open={open}
        title="Pedigree Settings"
        handleClose={() => setOpen(false)}
        showActions={true}
        onSave={() => {
          return onSave();
        }}
        minHeight={400}
        distanceTop={150}
        allowBackgroundClose={true}
      >
        <PedigreeCanvasSettingsForm
          preferences={preferences}
          setPreferences={setPreferences}
          members={members}
        />
      </CustomDialog>
    </>
  );
}

const PedigreeCanvasSettingsForm = ({
  preferences,
  setPreferences,
  members
}) => {
  const autoOn = !!preferences.auto_spacing;

  // When Auto spacing is on, derive the H×V the chart will use from the current
  // labels (same pure function the renderer applies), to show read-only in the
  // disabled Spacing fields. Recomputes as font/symbol/labels change in-modal.
  const auto = React.useMemo(
    () =>
      autoOn && Array.isArray(members) && members.length
        ? computeAutoSpacing(members, preferences)
        : null,
    // Depend on the fields that affect the result, not the whole `preferences`
    // object (which changes on every keystroke in any field).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoOn,
      members,
      preferences.font_size,
      preferences.symbol_size,
      preferences.labelsSet
    ]
  );
  // Displayed value of the combined Spacing slider. When Auto spacing is on it
  // shows the (read-only) computed horizontal percentage.
  const hValue = autoOn
    ? auto?.horizontalSpacingPct ?? preferences.horizontal_spacing
    : preferences.horizontal_spacing;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPreferences((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Sliders hide the raw px/% values (a clinician shouldn't have to reason about
  // pixels). The underlying preference keys + ranges are unchanged, so persistence
  // and applyCanvasPreferences are untouched.
  const setPref = (name, value) =>
    setPreferences((prev) => ({ ...prev, [name]: value }));
  // One combined Spacing slider drives BOTH axes proportionally (Tim's call) — at
  // any setting the standard 800:1200 H:V ratio is preserved.
  const setSpacing = (value) =>
    setPreferences((prev) => ({
      ...prev,
      horizontal_spacing: value,
      vertical_spacing: value
    }));

  const SLIDER_CAP_SX = {
    fontSize: 12,
    color: 'text.secondary',
    width: 60,
    flexShrink: 0
  };

  const handleReset = () => {
    setPreferences(DEFAULT_CANVAS_PREFERENCES);
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={SECTION_LABEL_SX}>Canvas</Box>
      <Box sx={CARD_SX}>
        <ConfigRow label="Symbol Size">
          <Box sx={SLIDER_CAP_SX}>Smaller</Box>
          <Slider
            size="small"
            value={Number(preferences.symbol_size) || 155}
            onChange={(_e, v) => setPref('symbol_size', v)}
            min={100}
            max={200}
            step={5}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>Larger</Box>
        </ConfigRow>

        <ConfigRow label="Font Size">
          <Box sx={SLIDER_CAP_SX}>Smaller</Box>
          <Slider
            size="small"
            value={Number(preferences.font_size) || 70}
            onChange={(_e, v) => setPref('font_size', v)}
            min={30}
            max={130}
            step={5}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>Larger</Box>
        </ConfigRow>

        <ConfigRow label="Spacing">
          <Box sx={SLIDER_CAP_SX}>Tighter</Box>
          <Slider
            size="small"
            value={
              autoOn
                ? Number(hValue) || 100
                : Number(preferences.horizontal_spacing) || 100
            }
            onChange={(_e, v) => setSpacing(v)}
            min={100}
            max={500}
            step={50}
            disabled={autoOn}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>Spacious</Box>
          {autoOn && (
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>auto</Box>
          )}
        </ConfigRow>

        <ConfigRow label="Auto spacing">
          <Switch
            name="auto_spacing"
            checked={autoOn}
            onChange={handleChange}
            size="small"
          />
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            {autoOn
              ? 'Spacing set automatically from labels'
              : 'Derive spacing from label density & length'}
          </Box>
        </ConfigRow>

        <ConfigRow label="Enable Grid" isLast>
          <Switch
            name="enable_grid"
            checked={!!preferences.enable_grid}
            onChange={handleChange}
            size="small"
          />
        </ConfigRow>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={handleReset}>
          Reset to defaults
        </Button>
      </Box>
    </Box>
  );
};

export default CanvasPreferences;
